import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import MicIcon from "@mui/icons-material/Mic";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PanToolIcon from "@mui/icons-material/PanTool";
import BoltIcon from "@mui/icons-material/Bolt";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import { streamRecruitmentChat, callRecruitmentAIAction } from "common/aiChatStream";
import MarkdownText from "common/MarkdownText";
import COLORS from "common/Colors";
import styled, { keyframes, css, createGlobalStyle } from "styled-components";

const PRIMARY = COLORS.PRIMARY;
const PRIMARY_DARK = "#1F3FB0";
const SURFACE = "#F4F6FD";
const BORDER = "#E5E9F7";

const bounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); opacity: .5; }
  40%           { transform: translateY(-5px); opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.35); }
  50%      { box-shadow: 0 0 0 6px rgba(255, 77, 79, 0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const SpeechRecognitionAPI =
  typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

const PanelWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
`;

const ResizeHandle = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  z-index: 5;

  &:hover, &.dragging {
    background: ${PRIMARY}22;
  }
`;

const RailOuter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const RailWrap = styled.div`
  width: 44px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 14px 0;
  cursor: pointer;
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(20, 26, 60, 0.08);

  &:hover { background: #FAFBFE; }
`;

const RailLabel = styled.div`
  writing-mode: vertical-rl;
  font-size: 12px;
  font-weight: 600;
  color: ${PRIMARY};
`;

const RailExpandButton = styled.button`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid ${BORDER};
  background: #fff;
  color: ${PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(20, 26, 60, 0.08);

  &:hover { background: #FAFBFE; }
  svg { font-size: 17px; }
`;

const PanelHeader = styled.div`
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${BORDER};
  flex-shrink: 0;
`;

const PanelHeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  font-weight: 600;
  color: ${COLORS.BLACK};
`;

const CollapseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  border-radius: 6px;
  color: ${COLORS.TEXT.PRIMARY};
  cursor: pointer;

  &:hover { background: #F0F2F9; color: ${COLORS.BLACK}; }
  svg { font-size: 17px; }
`;

// One button, two states — clicking toggles Approval <-> Auto instead of
// picking from a dropdown. Blue hand = ask before acting; amber bolt = act
// immediately. Tooltip carries the description so the icon alone stays compact.
const ExecutionModeButton = styled.button`
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background .15s ease, transform .1s ease;
  background: ${props => (props.$auto ? "#FFF1D6" : COLORS.SIDEBAR)};
  color: ${props => (props.$auto ? "#B8720A" : PRIMARY)};

  &:hover { filter: brightness(0.96); }
  &:active { transform: scale(0.94); }
  svg { font-size: 14px; }
`;

// ─── "Guide" modal — plain-English cheat sheet for what the assistant can
// be asked to do, opened from the question-mark button in the panel header.
// antd portals it to document.body, outside the ranking modal that carries
// the app's font override, so it needs the same rule scoped to itself.
const GuideModalStyle = createGlobalStyle`
  .guide-modal .ant-modal * {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }
`;

const GuideHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 22px;
  border-bottom: 1px solid ${BORDER};
`;

const GuideHeaderIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.SIDEBAR};
  color: ${PRIMARY};

  svg { font-size: 20px; }
`;

const GuideTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${COLORS.BLACK};
`;

const GuideSubtitle = styled.div`
  font-size: 12.5px;
  color: ${COLORS.TEXT.PRIMARY};
  margin-top: 2px;
`;

const GuideBody = styled.div`
  padding: 18px 22px 22px;
  max-height: 62vh;
  overflow-y: auto;
`;

const GuideStep = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;

  &:last-child { margin-bottom: 0; }
`;

const GuideStepNumber = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${PRIMARY};
  color: #fff;
  font-size: 11.5px;
  font-weight: 700;
`;

const GuideStepTitle = styled.div`
  font-size: 13.5px;
  font-weight: 700;
  color: ${COLORS.BLACK};
  margin-bottom: 4px;
`;

const GuideStepText = styled.div`
  font-size: 12.5px;
  line-height: 1.55;
  color: ${COLORS.TEXT.PRIMARY};

  code {
    background: ${COLORS.SIDEBAR};
    color: ${PRIMARY};
    border-radius: 4px;
    padding: 1px 5px;
    font-size: 12px;
  }

  strong { font-weight: 700; color: ${COLORS.BLACK}; }
`;

const GuideGroupLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: ${COLORS.BLACK};
  margin: 12px 0 6px;
`;

const GuideExample = styled.div`
  font-size: 12.5px;
  color: #35507A;
  background: ${COLORS.SIDEBAR}80;
  border-radius: 8px;
  padding: 7px 10px;
  margin-bottom: 6px;

  &:last-child { margin-bottom: 0; }
`;

const GuideModeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
`;

const GuideModeIcon = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => (props.$auto ? "#FFF1D6" : COLORS.SIDEBAR)};
  color: ${props => (props.$auto ? "#B8720A" : PRIMARY)};

  svg { font-size: 14px; }
`;

const Messages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  background: ${SURFACE};

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: ${BORDER}; border-radius: 8px; }
`;

const Bubble = styled.div`
  margin-bottom: 12px;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  justify-content: ${props => props.$user ? "flex-end" : "flex-start"};

  > div {
    max-width: 86%;
    padding: 10px 13px;
    line-height: 1.5;
    font-size: 13px;
    white-space: pre-wrap;
    ${props => props.$user
      ? `
        color: #fff;
        background: linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%);
        border-radius: 16px 16px 4px 16px;
        box-shadow: 0 3px 10px rgba(45, 83, 218, 0.28);
      `
      : `
        color: #333A4D;
        background: #fff;
        border: 1px solid ${BORDER};
        border-radius: 16px 16px 16px 4px;
        box-shadow: 0 1px 4px rgba(30, 42, 100, 0.06);
      `}
  }
`;

const BotAvatar = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.SIDEBAR};
  color: ${PRIMARY};

  svg { font-size: 14px; }
`;

const SuggestionChip = styled(Button)`
  border-radius: 999px;
  border: 1px solid ${COLORS.SIDEBAR};
  background: #fff;
  color: ${PRIMARY};
  font-size: 12px;
  height: auto;
  padding: 6px 12px;
  text-align: left;
  white-space: normal;

  &:hover, &:focus {
    border-color: ${PRIMARY};
    color: ${PRIMARY};
    background: ${COLORS.SECONDARY};
  }
`;

const TypingBubble = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 16px 16px 16px 4px;
  padding: 11px 16px;
  margin-bottom: 12px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${PRIMARY};
    animation: ${bounce} 1.2s infinite ease-in-out;
    &:nth-child(2) { animation-delay: .15s; }
    &:nth-child(3) { animation-delay: .3s; }
  }
`;

const StatusLine = styled.div`
  font-size: 11.5px;
  color: ${COLORS.TEXT.PRIMARY};
  margin: 0 0 12px 2px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const AccentCard = styled.div`
  background: #fff;
  border: 1px solid ${BORDER};
  border-left: 3px solid ${props => props.$accent || PRIMARY};
  border-radius: 4px 12px 12px 4px;
  padding: 12px 14px;
  margin-bottom: 14px;
  box-shadow: 0 2px 8px rgba(30, 42, 100, 0.07);
`;

const CardLabel = styled.div`
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: ${props => props.$accent || PRIMARY};
  margin-bottom: 6px;
`;

const CardBody = styled.div`
  font-size: 13px;
  color: #333A4D;
  margin-bottom: 10px;
  line-height: 1.45;
`;

// Shared by every AI-action progress card's gate (Approve & Continue /
// Cancel) — the bulk-email approval used to have its own one-off card;
// now every action type (email send, status update, scheduling, ...)
// pauses inside the same ProgressCard using these same buttons.
const ApprovalActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;
const ApprovalApproveButton = styled(Button)`
  border-radius: 999px;
  height: auto;
  padding: 8px 16px;
  font-weight: 600;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${PRIMARY} !important;
  border-color: ${PRIMARY} !important;
  color: #fff !important;

  &:hover, &:focus { background: ${PRIMARY_DARK} !important; border-color: ${PRIMARY_DARK} !important; color: #fff !important; }
`;
const ApprovalCancelButton = styled(Button)`
  border-radius: 999px;
  height: auto;
  padding: 8px 16px;
  font-weight: 600;
  font-size: 13px;
  border-color: ${BORDER};
  color: ${COLORS.TEXT.PRIMARY};

  &:hover { border-color: ${PRIMARY}; color: ${PRIMARY}; }
`;
const ApprovalReviewButton = styled(Button)`
  border-radius: 999px;
  height: auto;
  padding: 8px 16px;
  font-weight: 600;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border-color: ${PRIMARY};
  color: ${PRIMARY};

  &:hover, &:focus { background: ${PRIMARY}10; border-color: ${PRIMARY_DARK}; color: ${PRIMARY_DARK}; }
`;

// ─── Global AI action progress card — one shared design for every
// chatbot-triggered action (analysis, ranking, email, status changes,
// scheduling, follow-ups, ...), driven entirely by ACTION_FLOWS + the
// pushProgressCard/patchProgressCard helpers below.
const haloBlink = keyframes`
  0%   { transform: scale(0.85); opacity: .6; }
  70%  { transform: scale(1.7); opacity: 0; }
  100% { transform: scale(1.7); opacity: 0; }
`;

const dotPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 ${PRIMARY}40; }
  50%      { box-shadow: 0 0 0 5px ${PRIMARY}00; }
`;

const spinIcon = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const ProgressCardWrap = styled.div`
  max-width: 620px;
  margin: 0 auto 14px;
  padding: 4px 4px 20px;
`;

const ProgressHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding-bottom: 14px;
  margin-bottom: 16px;
  border-bottom: 1px solid ${BORDER};
`;

const ProgressHeaderIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.SIDEBAR};
  color: ${PRIMARY};

  svg { font-size: 19px; }
`;

const ProgressHeaderTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${COLORS.BLACK};
`;

const ProgressHeaderDesc = styled.div`
  font-size: 12.5px;
  color: ${COLORS.TEXT.PRIMARY};
  margin-top: 3px;
  line-height: 1.4;
`;

const ProgressMetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
`;

const ProgressMetaLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProgressMetaIcon = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.SIDEBAR};
  color: ${PRIMARY};

  svg { font-size: 14px; }
`;

const ProgressMetaLabel = styled.div`
  font-size: 12.5px;
  font-weight: 700;
  color: ${COLORS.BLACK};
`;

const ProgressMetaRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11.5px;
  color: ${COLORS.TEXT.PRIMARY};
  flex-shrink: 0;
  white-space: nowrap;

  svg { font-size: 13px; }
`;

const ProgressStatLine = styled.div`
  font-size: 15px;
  color: ${COLORS.TEXT.PRIMARY};
  margin-bottom: 10px;

  strong { font-weight: 700; color: ${COLORS.BLACK}; }
`;

const ProgressBarTrack = styled.div`
  height: 8px;
  border-radius: 999px;
  background: ${COLORS.SIDEBAR};
  overflow: hidden;
  margin-bottom: 16px;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background: ${PRIMARY};
  border-radius: 999px;
  width: ${props => props.$pct}%;
  transition: width .3s ease;
`;

const CurrentItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: ${COLORS.SIDEBAR}80;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 18px;
`;

const CurrentItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const CurrentItemAvatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  color: ${PRIMARY};
  font-size: 12.5px;
  font-weight: 700;
`;

const CurrentItemText = styled.div`
  font-size: 13.5px;
  font-weight: 700;
  color: ${COLORS.BLACK};
`;

const CurrentItemSub = styled.div`
  font-size: 11.5px;
  color: ${COLORS.TEXT.PRIMARY};
  margin-top: 1px;
`;

const CurrentItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: ${COLORS.TEXT.PRIMARY};
  margin-top: 2px;

  svg { font-size: 12px; }
`;

const PillSpinner = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid ${PRIMARY}33;
  border-top-color: ${PRIMARY};
  animation: ${spin} .7s linear infinite;
  flex-shrink: 0;
`;

const CurrentItemStatusPill = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  background: #fff;
  color: ${props => (props.$done ? COLORS.SUCCESS : PRIMARY)};
  font-size: 12px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 999px;
  white-space: nowrap;

  svg { font-size: 15px; }
`;

const STEP_CIRCLE_SIZE = 30;

const StepRow = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
`;

// Each step owns a slice of the row and draws its own half-lines
// (::before to the left of its circle, ::after to the right) at the
// circle's exact vertical center — so the connector runs straight through
// every dot's midpoint no matter how wide a neighboring label is, instead
// of stopping short at the edge of a flex column.
const StepNodeCol = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 4px;

  &::before, &::after {
    content: "";
    position: absolute;
    top: ${STEP_CIRCLE_SIZE / 2 - 1}px;
    height: 2px;
    transition: background .25s ease;
  }
  &::before {
    left: 0;
    right: 50%;
    margin-right: ${STEP_CIRCLE_SIZE / 2 + 4}px;
    background: ${props => (props.$isFirst ? "transparent" : (props.$leftFilled ? COLORS.SUCCESS : BORDER))};
  }
  &::after {
    left: 50%;
    right: 0;
    margin-left: ${STEP_CIRCLE_SIZE / 2 + 4}px;
    background: ${props => (props.$isLast ? "transparent" : (props.$rightFilled ? COLORS.SUCCESS : BORDER))};
  }
`;

const StepCircle = styled.div`
  position: relative;
  z-index: 1;
  width: ${STEP_CIRCLE_SIZE}px;
  height: ${STEP_CIRCLE_SIZE}px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  transition: background .25s ease, border-color .25s ease, color .25s ease;

  ${props => props.$state === "done" && css`
    background: ${COLORS.SUCCESS}; border: 2px solid ${COLORS.SUCCESS}; color: #fff;
  `}
  ${props => props.$state === "active" && css`
    background: ${PRIMARY}; border: 2px solid ${PRIMARY}; color: #fff;

    &::before, &::after {
      content: "";
      position: absolute;
      inset: -6px;
      border-radius: 50%;
      background: ${PRIMARY}55;
      z-index: -1;
      animation: ${haloBlink} 1.8s ease-out infinite;
    }
    &::after { animation-delay: .6s; }
  `}
  ${props => props.$state === "waiting" && css`
    background: #fff; border: 2px solid ${COLORS.WARNING}; color: ${COLORS.WARNING};
  `}
  ${props => props.$state === "pending" && css`
    background: #fff; border: 2px solid ${BORDER}; color: #B0B5C4;
  `}
`;

const StepActiveIcon = styled(AutorenewIcon)`
  && {
    font-size: 15px;
    animation: ${spinIcon} 1.1s linear infinite;
  }
`;

const StepLabel = styled.div`
  width: 100%;
  font-size: 11.5px;
  font-weight: 600;
  text-align: center;
  line-height: 1.3;
  color: ${props => (props.$muted ? "#9098B8" : COLORS.BLACK)};
  margin-top: 8px;
`;

const StepStatusText = styled.div`
  width: 100%;
  font-size: 10.5px;
  font-weight: 600;
  text-align: center;
  color: ${props => props.$color || "#9098B8"};
  margin-top: 2px;
`;

const ProgressNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  color: #35507A;
  background: ${COLORS.SIDEBAR}80;
  border-radius: 10px;
  padding: 10px 12px;

  svg { font-size: 15px; color: ${PRIMARY}; flex-shrink: 0; margin-top: 1px; }
`;

// ─── Compact mirror shown inline in the chat thread — links back to the
// full ProgressCard living in Section 1 instead of duplicating it.
const MiniCardWrap = styled.div`
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 14px;
  padding: 14px;
  margin-bottom: 12px;
  max-width: 86%;
  box-shadow: 0 1px 4px rgba(30, 42, 100, 0.06);
`;

const MiniHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;

const MiniHeaderIcon = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.SIDEBAR};
  color: ${PRIMARY};

  svg { font-size: 14px; }
`;

const MiniTitle = styled.div`
  font-size: 12.5px;
  font-weight: 700;
  color: ${COLORS.BLACK};
`;

const MiniStat = styled.div`
  font-size: 11px;
  color: ${COLORS.TEXT.PRIMARY};
  margin-top: 1px;
`;

const MiniBarTrack = styled.div`
  height: 5px;
  border-radius: 999px;
  background: ${COLORS.SIDEBAR};
  overflow: hidden;
  margin-bottom: 10px;
`;

const MiniBarFill = styled.div`
  height: 100%;
  background: ${PRIMARY};
  border-radius: 999px;
  width: ${props => props.$pct}%;
  transition: width .3s ease;
`;

const MiniItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
`;

const MiniItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const MiniAvatar = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.SIDEBAR};
  color: ${PRIMARY};
  font-size: 10px;
  font-weight: 700;
`;

const MiniItemName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.BLACK};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MiniItemStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10.5px;
  font-weight: 600;
  color: ${props => (props.$done ? COLORS.SUCCESS : PRIMARY)};
  flex-shrink: 0;
  white-space: nowrap;
`;

const MiniDotsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  margin-bottom: 12px;
`;

const MiniDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${props => (props.$state === "pending" ? BORDER : props.$state === "active" ? PRIMARY : COLORS.SUCCESS)};
  ${props => props.$state === "active" && css`animation: ${dotPulse} 1.4s infinite;`}
`;

const MiniDotLine = styled.div`
  flex: 1;
  height: 2px;
  background: ${props => (props.$filled ? COLORS.SUCCESS : BORDER)};
`;

const MiniCtaButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  border: none;
  background: ${COLORS.SIDEBAR}80;
  color: ${PRIMARY};
  font-size: 12px;
  font-weight: 600;
  padding: 8px 10px;
  border-radius: 999px;
  cursor: pointer;
  font-family: inherit;

  svg { font-size: 13px; }
  &:hover { background: ${COLORS.SIDEBAR}; }
`;

const ProgressGate = styled.div`
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 14px;
  background: #FFF8E8;
  border: 1px solid #F5D98A;
  border-radius: 12px;
  padding: 14px 18px;
`;

const ProgressGateText = styled.div`
  flex: 1 1 220px;
  min-width: 200px;
`;

const ProgressGateLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: ${COLORS.WARNING};
  text-transform: uppercase;
  letter-spacing: .04em;
  margin-bottom: 6px;
`;

const ProgressGateMessage = styled.div`
  font-size: 12.5px;
  color: ${COLORS.BLACK};
`;

const ProgressCta = styled(Button)`
  display: block;
  margin: 14px auto 0;
  border-radius: 999px;
  height: auto;
  padding: 7px 16px;
  font-weight: 600;
  font-size: 12.5px;
  background: ${PRIMARY} !important;
  border-color: ${PRIMARY} !important;
  color: #fff !important;

  &:hover, &:focus { background: ${PRIMARY_DARK} !important; border-color: ${PRIMARY_DARK} !important; color: #fff !important; }
`;

const ScoreCard = styled.div`
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 14px;
  padding: 14px 16px;
  margin-bottom: 14px;
  box-shadow: 0 2px 8px rgba(30, 42, 100, 0.07);
`;

const ScoreCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
`;

const ScoreBadge = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px solid ${props => props.$color};
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  line-height: 1;

  strong { font-size: 14px; }
  span { font-size: 8px; opacity: .8; }
`;

const ScoreSummary = styled.div`
  font-size: 12px;
  color: #333A4D;
  line-height: 1.4;
  margin-bottom: 8px;
`;

const ScoreSection = styled.div`
  margin-top: 8px;

  ul { margin: 4px 0 0; padding-left: 18px; }
  li { font-size: 11.5px; color: #333A4D; line-height: 1.5; }
`;

const ReportGeneratedLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: ${COLORS.SUCCESS};
  font-weight: 600;
  margin: 0 0 8px 30px;

  svg { font-size: 14px; }
`;

const ReportCardWrap = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: calc(100% - 30px);
  margin-left: 30px;
  text-align: left;
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 14px;
  padding: 12px 14px;
  margin-bottom: 14px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(30, 42, 100, 0.07);
  transition: border-color .15s ease, box-shadow .15s ease;
  font-family: inherit;

  &:hover, &:focus {
    border-color: ${PRIMARY};
    box-shadow: 0 4px 14px rgba(45, 83, 218, 0.18);
    outline: none;
  }
`;

const ReportCardIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.SIDEBAR};
  color: ${PRIMARY};

  svg { font-size: 19px; }
`;

const ReportCardTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #333A4D;
`;

const ReportCardJob = styled.div`
  font-size: 11.5px;
  color: ${COLORS.TEXT.PRIMARY};
  margin-top: 1px;
`;

const ReportCardMeta = styled.div`
  font-size: 11.5px;
  color: ${COLORS.TEXT.PRIMARY};
`;

const ReportCardLink = styled.div`
  font-size: 11.5px;
  font-weight: 600;
  color: ${PRIMARY};
  margin-top: 4px;
`;

const Footer = styled.div`
  padding: 12px;
  border-top: 1px solid ${BORDER};
  display: flex;
  gap: 6px;
  background: #fff;
  flex-shrink: 0;
`;

const EditableInput = styled.div`
  flex: 1;
  min-width: 0;
  max-height: 90px;
  overflow-y: auto;
  border-radius: 16px;
  padding: 8px 14px;
  border: 1px solid ${BORDER};
  background: ${SURFACE};
  font-size: 13px;
  line-height: 1.5;
  outline: none;
  cursor: text;
  white-space: pre-wrap;
  word-break: break-word;

  &:hover, &:focus {
    border-color: ${PRIMARY};
    background: #fff;
  }

  &:empty:before {
    content: attr(data-placeholder);
    color: #9098B8;
  }
`;

const MentionChipStyle = {
  display: "inline-block",
  background: COLORS.SIDEBAR,
  color: PRIMARY_DARK,
  borderRadius: "6px",
  padding: "1px 6px",
  margin: "0 1px",
  fontWeight: "600",
  fontSize: "12.5px",
  userSelect: "none",
};

const VoiceButton = styled(Button)`
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid ${BORDER};
  background: #fff;
  color: ${PRIMARY};

  &:hover, &:focus {
    border-color: ${PRIMARY};
    color: ${PRIMARY};
  }

  ${props => props.$listening && css`
    border-color: ${COLORS.ERROR};
    color: ${COLORS.ERROR};
    animation: ${pulse} 1.3s infinite;
  `}
`;

const MentionList = styled.div`
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 100%;
  margin-bottom: 8px;
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(20, 26, 60, 0.14);
  max-height: 180px;
  overflow-y: auto;
  z-index: 10;
`;

const MentionItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 8px 7px 14px;
  background: ${props => props.$active ? SURFACE : "transparent"};

  &:hover { background: ${SURFACE}; }
`;

const MentionItemText = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: #333A4D;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MentionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const MentionActionBtn = styled.button`
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${BORDER};
  border-radius: 50%;
  background: #fff;
  color: ${PRIMARY};
  cursor: pointer;

  &:hover { background: ${COLORS.SIDEBAR}; border-color: ${PRIMARY}; }
  svg { font-size: 14px; }
`;

const MentionEmpty = styled.div`
  padding: 9px 14px;
  font-size: 12px;
  color: ${COLORS.TEXT.PRIMARY};
`;

const SendButton = styled(Button)`
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%);

  &:hover, &:focus {
    background: linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%) !important;
  }
`;

function applyProposal(action, payload) {
  switch (action) {
    case "propose_candidate_stage_update":
      return CALL_API(`recruitment/candidate/${payload.candidateId}`, "patch", {
        currentStatus: payload.newStageIndex,
      });
    case "propose_job_status_update":
      return CALL_API(`recruitment/jobs/${payload.jobId}`, "patch", {
        jobStatus: payload.newJobStatus,
      });
    case "propose_hiring_process_step":
      return CALL_API(`recruitment/hiringprocess/${payload.processId}`, "patch", {
        steps: payload.updatedSteps,
      });
    case "propose_talent_pool_flag":
      return CALL_API(`recruitment/candidate/${payload.candidateId}`, "patch", {
        talentPoolStatus: payload.talentPoolStatus,
      });
    case "propose_candidate_note":
      return CALL_API(`recruitment/candidate/${payload.candidateId}`, "patch", {
        notes: payload.updatedNotes,
      });
    default:
      return Promise.resolve(null);
  }
}

function refreshCallbackFor(action, { onCandidateUpdated, onJobUpdated }) {
  if (action === "propose_job_status_update") return onJobUpdated;
  return onCandidateUpdated;
}

function scoreColor(score) {
  if (score >= 8) return COLORS.SUCCESS;
  if (score >= 6) return PRIMARY;
  if (score >= 4) return COLORS.WARNING;
  return COLORS.ERROR;
}

function ResumeScoreCard({ card }) {
  const color = scoreColor(card.score);
  return (
    <ScoreCard>
      <ScoreCardHeader>
        <ScoreBadge $color={color}>
          <strong>{card.score}</strong>
          <span>/ 10</span>
        </ScoreBadge>
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: "#333A4D" }}>{card.candidateName}</div>
          {card.jobTitle && <div style={{ fontSize: 10.5, color: COLORS.TEXT.PRIMARY }}>{card.jobTitle}</div>}
        </div>
      </ScoreCardHeader>

      {card.summary && <ScoreSummary>{card.summary}</ScoreSummary>}

      {card.strengths?.length > 0 && (
        <ScoreSection>
          <CardLabel $accent={COLORS.SUCCESS}>Strengths</CardLabel>
          <ul>{card.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </ScoreSection>
      )}

      {card.gaps?.length > 0 && (
        <ScoreSection>
          <CardLabel $accent={COLORS.WARNING}>Gaps</CardLabel>
          <ul>{card.gaps.map((g, i) => <li key={i}>{g}</li>)}</ul>
        </ScoreSection>
      )}
    </ScoreCard>
  );
}

const REPORT_KIND = {
  rank: { label: "Ranking Report", generatedLine: "Ranking report generated", icon: BarChartOutlinedIcon },
  insights: { label: "Insights Report", generatedLine: "Insights report generated", icon: InsightsOutlinedIcon },
  email: { label: "Email Report", generatedLine: "Email report generated", icon: MailOutlineIcon },
};

// Clickable chat-history card left behind after a mention-triggered ranking
// or insights action, or a chat-parsed bulk-email request, completes —
// reopens that (cached) report in Section 1 without re-running the AI call.
function ReportCard({ card, onOpen }) {
  const { t } = useTranslation();
  const kind = REPORT_KIND[card.mode] || REPORT_KIND.rank;
  const Icon = kind.icon;
  return (
    <div>
      <ReportGeneratedLine>
        <CheckOutlinedIcon />
        {t(kind.generatedLine)}
      </ReportGeneratedLine>
      <ReportCardWrap type="button" onClick={onOpen}>
        <ReportCardIcon>
          <Icon />
        </ReportCardIcon>
        <div>
          <ReportCardTitle>{t(kind.label)}</ReportCardTitle>
          <ReportCardJob>{card.jobTitle}</ReportCardJob>
          {card.mode === "email" && typeof card.count === "number" && (
            <ReportCardMeta>
              {card.count} {card.count === 1 ? t("shortlisted candidate") : t("shortlisted candidates")}
            </ReportCardMeta>
          )}
          <ReportCardLink>{t("View report")} →</ReportCardLink>
        </div>
      </ReportCardWrap>
    </div>
  );
}

function initialsFor(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

// Single source of truth for every AI action's progress card: title,
// description, icon and the 3-6 steps it moves through. Adding a new
// action type (once its backend/UI exists) is just a new entry here plus
// a pushProgressCard(key, ...) call at its trigger site.
const ACTION_FLOWS = {
  resume_analysis: {
    icon: AutoAwesomeIcon,
    title: "Analyzing candidate resume",
    description: "I'm reviewing the candidate's resume and comparing it with the job requirements.",
    steps: ["Reading resume", "Analyzing skills", "Matching with JD", "Generating report"],
  },
  ranking: {
    icon: BarChartOutlinedIcon,
    title: "Ranking candidates",
    description: "I'm scoring every candidate against this job's requirements.",
    steps: ["Fetching candidates", "Scoring candidates", "Ranking results", "Generating report"],
  },
  insights: {
    icon: InsightsOutlinedIcon,
    title: "Generating insights",
    description: "I'm scoring candidates and building the insight breakdown for this job.",
    steps: ["Fetching candidates", "Scoring candidates", "Building insights", "Generating report"],
  },
  conditional_ranking: {
    icon: InsightsOutlinedIcon,
    title: "Ranking candidates",
    description: "I'm filtering candidates by your condition, then scoring and ranking them.",
    steps: ["Parsing condition", "Filtering candidates", "Scoring matches", "Ranking results"],
  },
  email_generation: {
    icon: MailOutlineIcon,
    title: "Drafting emails",
    description: "I'm drafting a personalized email for each selected candidate.",
    steps: ["Selecting candidates", "Drafting emails", "Personalizing content", "Ready for review"],
  },
  email_sending: {
    icon: SendOutlinedIcon,
    title: "Sending emails",
    description: "I'm sending the approved emails to each candidate.",
    steps: ["Preparing batch", "Sending emails", "Confirming delivery"],
  },
  comparison: {
    icon: CompareArrowsIcon,
    title: "Comparing candidates",
    description: "I'm comparing the selected candidates side by side.",
    steps: ["Loading profiles", "Comparing skills", "Comparing experience", "Generating summary"],
  },
  status_update: {
    icon: SwapHorizOutlinedIcon,
    title: "Updating status",
    description: "I'm updating the candidate's stage.",
    steps: ["Identifying candidate", "Applying update", "Refreshing records"],
  },
  scheduling: {
    icon: EventOutlinedIcon,
    title: "Scheduling interview",
    description: "I'm finding a slot and creating the interview invite.",
    steps: ["Checking availability", "Creating slot", "Sending invite"],
  },
  followup: {
    icon: NotificationsActiveOutlinedIcon,
    title: "Sending follow-ups",
    description: "I'm identifying non-responders and sending reminders.",
    steps: ["Identifying non-responders", "Drafting reminders", "Sending"],
  },
};

// The one progress-card design used by every action type above. `card`
// carries the live/mutable fields (activeIndex, completed, bulk,
// currentItem, awaitingApproval, ...); ACTION_FLOWS only supplies the
// static title/description/steps a card is seeded with.
// Shared step-state resolver — both the full ProgressCard (Section 1) and
// the MiniProgressCard (chat mirror) render the same 5 states off the same
// card object, so they can never drift out of sync with each other.
function stepStateAt(card, i, activeIndex) {
  if (card.awaitingApproval && i === activeIndex) return "waiting";
  if (i < activeIndex || card.completed) return "done";
  if (i === activeIndex) return "active";
  return "pending";
}

export function ProgressCard({ card }) {
  const { t } = useTranslation();
  const steps = card.steps || [];
  const total = steps.length;
  const activeIndex = card.completed ? total - 1 : Math.min(card.activeIndex ?? 0, Math.max(total - 1, 0));
  const bulkPct = card.bulk ? Math.min(100, Math.round((card.bulk.current / Math.max(card.bulk.total, 1)) * 100)) : null;
  const stepPct = total > 1 ? Math.round((activeIndex / (total - 1)) * 100) : (card.completed ? 100 : 0);
  const pct = bulkPct ?? stepPct;
  const currentStepLabel = steps[activeIndex];

  return (
    <ProgressCardWrap>
      <ProgressHeader>
        <ProgressHeaderIcon>{card.icon ? <card.icon /> : <AutoAwesomeIcon />}</ProgressHeaderIcon>
        <div>
          <ProgressHeaderTitle>{card.title}</ProgressHeaderTitle>
          {card.description && <ProgressHeaderDesc>{card.description}</ProgressHeaderDesc>}
        </div>
      </ProgressHeader>

      <ProgressMetaRow>
        <ProgressMetaLeft>
          <ProgressMetaIcon><DescriptionOutlinedIcon /></ProgressMetaIcon>
          <ProgressMetaLabel>{card.metaLabel || t("Overall progress")}</ProgressMetaLabel>
        </ProgressMetaLeft>
        {card.eta && (
          <ProgressMetaRight>
            <AccessTimeIcon />
            {card.eta}
          </ProgressMetaRight>
        )}
      </ProgressMetaRow>

      <ProgressStatLine>
        {card.bulk
          ? <>
              <strong>{card.bulk.current} / {card.bulk.total}</strong> {card.bulk.unit} {card.bulk.verb || t("analyzed")} · {pct}%
            </>
          : <>{t("Step {{n}} of {{total}}", { n: Math.min(activeIndex + 1, total), total })} · {pct}%</>
        }
      </ProgressStatLine>

      <ProgressBarTrack>
        <ProgressBarFill $pct={pct} />
      </ProgressBarTrack>

      {card.currentItem && (
        <CurrentItemRow>
          <CurrentItemLeft>
            <CurrentItemAvatar>{initialsFor(card.currentItem.title)}</CurrentItemAvatar>
            <div style={{ minWidth: 0 }}>
              <CurrentItemText>{card.currentItem.title}</CurrentItemText>
              {card.currentItem.subtitle && <CurrentItemSub>{card.currentItem.subtitle}</CurrentItemSub>}
              {card.currentItem.meta && (
                <CurrentItemMeta><DescriptionOutlinedIcon />{card.currentItem.meta}</CurrentItemMeta>
              )}
            </div>
          </CurrentItemLeft>
          <CurrentItemStatusPill $done={card.completed}>
            {card.completed
              ? <><CheckOutlinedIcon />{t("Done")}</>
              : <><PillSpinner />{currentStepLabel ? t(currentStepLabel) : t("Working…")}…</>}
          </CurrentItemStatusPill>
        </CurrentItemRow>
      )}

      <StepRow>
        {steps.map((label, i) => {
          const state = stepStateAt(card, i, activeIndex);
          const passed = i < activeIndex || card.completed;
          return (
            <StepNodeCol
              key={i}
              $isFirst={i === 0}
              $isLast={i === steps.length - 1}
              $leftFilled={i > 0 && (i - 1 < activeIndex || card.completed)}
              $rightFilled={passed}
            >
              <StepCircle $state={state}>
                {state === "done"
                  ? <CheckOutlinedIcon style={{ fontSize: 14 }} />
                  : state === "waiting"
                  ? <PanToolIcon style={{ fontSize: 12 }} />
                  : state === "active"
                  ? <StepActiveIcon />
                  : i + 1}
              </StepCircle>
              <StepLabel $muted={state === "pending"}>{t(label)}</StepLabel>
              <StepStatusText
                $color={state === "done" ? COLORS.SUCCESS : state === "active" ? PRIMARY : state === "waiting" ? COLORS.WARNING : "#9098B8"}
              >
                {state === "done" ? t("Completed") : state === "active" ? t("In progress…") : state === "waiting" ? t("Waiting…") : t("Pending")}
              </StepStatusText>
            </StepNodeCol>
          );
        })}
      </StepRow>

      {card.awaitingApproval ? (
        <ProgressGate>
          <ProgressGateText>
            <ProgressGateLabel>{t("Approval needed")}</ProgressGateLabel>
            <ProgressGateMessage>{card.gateMessage}</ProgressGateMessage>
          </ProgressGateText>
          <ApprovalActions>
            {card.ctaLabel && card.onCta && (
              <ApprovalReviewButton onClick={card.onCta}>
                <MailOutlineIcon style={{ fontSize: 14 }} />
                {card.ctaLabel}
              </ApprovalReviewButton>
            )}
            <ApprovalApproveButton
              icon={<CheckOutlinedIcon style={{ fontSize: 13 }} />}
              loading={card.gateBusy}
              onClick={card.onApprove}
            >
              {card.gateApproveLabel || t("Approve & Continue")}
            </ApprovalApproveButton>
            <ApprovalCancelButton disabled={card.gateBusy} onClick={card.onCancel}>
              {t("Cancel")}
            </ApprovalCancelButton>
          </ApprovalActions>
        </ProgressGate>
      ) : card.completed ? (
        card.ctaLabel && <ProgressCta onClick={card.onCta}>{card.ctaLabel} →</ProgressCta>
      ) : (
        card.note && (
          <ProgressNote>
            <InfoOutlinedIcon />
            {card.note}
          </ProgressNote>
        )
      )}

      {card.resumeResult && (
        <div style={{ marginTop: 16 }}>
          <ResumeScoreCard card={card.resumeResult} />
        </div>
      )}
    </ProgressCardWrap>
  );
}

// Compact mirror of the card above, rendered inline in the chat thread —
// same underlying `card` object, same step states, just condensed. Links
// back to Section 1 (already visible alongside the chat in this modal)
// instead of duplicating the full card in the conversation.
export function MiniProgressCard({ card, onShow }) {
  const { t } = useTranslation();
  const steps = card.steps || [];
  const total = steps.length;
  const activeIndex = card.completed ? total - 1 : Math.min(card.activeIndex ?? 0, Math.max(total - 1, 0));
  const bulkPct = card.bulk ? Math.min(100, Math.round((card.bulk.current / Math.max(card.bulk.total, 1)) * 100)) : null;
  const stepPct = total > 1 ? Math.round((activeIndex / (total - 1)) * 100) : (card.completed ? 100 : 0);
  const pct = bulkPct ?? stepPct;
  const currentStepLabel = steps[activeIndex];

  return (
    <MiniCardWrap>
      <MiniHeaderRow>
        <MiniHeaderIcon>{card.icon ? <card.icon /> : <AutoAwesomeIcon />}</MiniHeaderIcon>
        <div>
          <MiniTitle>{card.title}</MiniTitle>
          <MiniStat>
            {card.bulk
              ? t("{{current}} / {{total}} completed · {{pct}}%", { current: card.bulk.current, total: card.bulk.total, pct })
              : t("Step {{n}} of {{total}} · {{pct}}%", { n: Math.min(activeIndex + 1, total), total, pct })}
          </MiniStat>
        </div>
      </MiniHeaderRow>

      <MiniBarTrack>
        <MiniBarFill $pct={pct} />
      </MiniBarTrack>

      {card.currentItem && (
        <MiniItemRow>
          <MiniItemLeft>
            <MiniAvatar>{initialsFor(card.currentItem.title)}</MiniAvatar>
            <MiniItemName>{card.currentItem.title}</MiniItemName>
          </MiniItemLeft>
          <MiniItemStatus $done={card.completed}>
            {card.completed
              ? <><CheckOutlinedIcon style={{ fontSize: 12 }} />{t("Done")}</>
              : <><PillSpinner />{currentStepLabel ? t(currentStepLabel) : t("Working…")}…</>}
          </MiniItemStatus>
        </MiniItemRow>
      )}

      <MiniDotsRow>
        {steps.map((_, i) => {
          const state = stepStateAt(card, i, activeIndex);
          return (
            <React.Fragment key={i}>
              <MiniDot $state={state} />
              {i < steps.length - 1 && <MiniDotLine $filled={i < activeIndex || card.completed} />}
            </React.Fragment>
          );
        })}
      </MiniDotsRow>

      {onShow && (
        <MiniCtaButton type="button" onClick={onShow}>
          {t("View progress")}
          <NorthEastIcon />
        </MiniCtaButton>
      )}
    </MiniCardWrap>
  );
}

// Docked chat panel for the AI Resume Ranker modal — scoped entirely to the
// currently ranked/filtered candidates (jobId/jobTitle/candidates passed in
// from JobsList). Unlike the old floating chatbot, this never answers general
// jobs/talent-pool/hiring-process questions; it only exists to help with the
// candidates currently shown in the modal.
function activeMentionQuery(value) {
  const m = /(?:^|\s)@([^\s@]*)$/.exec(value || "");
  return m ? m[1] : null;
}

function RecruitmentChatPanel({
  active,
  jobId,
  jobTitle,
  jobs,
  onSelectJob,
  candidates,
  selectedCandidateId,
  activeSection,
  emailReportSummary,
  filterDescription,
  requestBulkEmail,
  onSendAllEmails,
  onOpenEmailReport,
  onViewCandidate,
  onShowProgress,
  onCandidateUpdated,
  onJobUpdated,
  onActionProgressChange,
  onProgressCardsChange,
  bulkProgress,
  collapsed,
  onToggleCollapse,
  maxHeight,
}) {
  const { t } = useTranslation();
  // Approval Mode (default) asks before sending emails or applying a
  // proposed change; Auto Mode executes them immediately. In-memory only —
  // resets with a fresh chat session, matching "for the current
  // recruitment-agent session" rather than persisting across page reloads.
  const [executionMode, setExecutionMode] = useState("approval");
  const [editableText, setEditableText] = useState("");
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0);
  const editableRef = useRef(null);
  const [messages, setMessages] = useState([
    {
      assistant: "Hi! I'm your AI assistant. Ask me anything about this candidate, job requirements, or get insights, suggestions and comparisons.",
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [clarification, setClarification] = useState(null);
  const [pendingProposal, setPendingProposal] = useState(null);
  const [applyingProposal, setApplyingProposal] = useState(false);
  const [listening, setListening] = useState(false);
  const [panelWidth, setPanelWidth] = useState(320);
  const [resizing, setResizing] = useState(false);
  const resizeStartRef = useRef({ x: 0, width: 320 });
  const conversationHistoryRef = useRef([]);
  const containerRef = useRef(null);
  const recognitionRef = useRef(null);
  const progressCounterRef = useRef(0);
  const progressTimersRef = useRef({});
  const resumeProgressIdRef = useRef(null);
  const [actionProgress, setActionProgress] = useState(null);
  // Every action's card, keyed by id, so the transcript keeps each one
  // where it happened — `actionProgress` is only the latest/live one.
  const [progressCards, setProgressCards] = useState({});
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages, loading, statusText, pendingProposal, actionProgress, bulkProgress, collapsed]);

  useEffect(() => () => {
    const recognition = recognitionRef.current;
    if (!recognition) return;
    recognition.manualStop = true;
    recognition.stop();
  }, []);
  useEffect(() => () => { Object.values(progressTimersRef.current).forEach(clearInterval); }, []);

  // A progress card belongs to whichever job it was generated for — switching
  // jobs (job switcher or a fresh @mention) must drop any leftover card, or
  // it keeps rendering alongside the new job's own tabs/report (e.g. a
  // finished "Sending emails" card from job A still showing next to job B's
  // Insights tab).
  const prevJobIdRef = useRef(jobId);
  useEffect(() => {
    if (prevJobIdRef.current === jobId) return;
    prevJobIdRef.current = jobId;
    Object.values(progressTimersRef.current).forEach(clearInterval);
    progressTimersRef.current = {};
    resumeProgressIdRef.current = null;
    setActionProgress(null);
  }, [jobId]);

  // Mirrors the currently-running action's progress card up to JobsList,
  // which renders it in Section 1 (Playground) — it never appears in the
  // chat thread itself.
  useEffect(() => {
    onActionProgressChange?.(actionProgress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionProgress]);

  // Section 1 can be pointed at any past action, so it needs the whole
  // registry — holding a copy of one card would freeze it mid-flight.
  useEffect(() => {
    onProgressCardsChange?.(progressCards);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressCards]);

  // ─── Global AI action progress engine ────────────────────────────────
  // Every chatbot-triggered action (analysis, ranking, email, status
  // change, ...) drives this single card through the same lifecycle: push
  // it, tick it forward while the one real backend call is in flight, then
  // either gate it (Approval Mode) or complete it. It lives in its own
  // state (not the chat `messages`) and is mirrored up to JobsList via
  // onActionProgressChange, which renders it in Section 1 (Playground) —
  // the chat history itself never shows it.
  const patchProgressCard = (id, patch) => {
    const applyPatch = (card) => ({ ...card, ...(typeof patch === "function" ? patch(card) : patch) });
    setActionProgress(prev => (prev && prev.id === id ? applyPatch(prev) : prev));
    setProgressCards(prev => (prev[id] ? { ...prev, [id]: applyPatch(prev[id]) } : prev));
  };

  const pushProgressCard = (flowKey, overrides = {}) => {
    const id = `progress-${++progressCounterRef.current}`;
    const flow = ACTION_FLOWS[flowKey] || {};
    const card = {
      id,
      flowKey,
      icon: flow.icon,
      title: flow.title,
      description: flow.description,
      steps: flow.steps || [],
      activeIndex: 0,
      completed: false,
      awaitingApproval: false,
      bulk: null,
      currentItem: null,
      note: t("This will take a few seconds. You can continue chatting in the meantime."),
      ctaLabel: null,
      onCta: null,
      ...overrides,
    };
    setActionProgress(card);
    // Each action also gets its own card pinned into the transcript, so a
    // later action doesn't wipe the record of the one before it.
    setProgressCards(prev => ({ ...prev, [id]: card }));
    setMessages(prev => [...prev, { progressCardId: id }]);
    return id;
  };

  // Ticks the active step forward on a timer, never advancing past `cap` —
  // used so the card visibly moves while we wait on one backend call that
  // only ever resolves once, at the very end, instead of sitting frozen.
  const startProgressTicker = (id, cap, stepMs = 800) => {
    stopProgressTicker(id);
    const timer = setInterval(() => {
      let reachedCap = false;
      patchProgressCard(id, (card) => {
        if (card.activeIndex >= cap) { reachedCap = true; return card; }
        return { activeIndex: card.activeIndex + 1 };
      });
      if (reachedCap) stopProgressTicker(id);
    }, stepMs);
    progressTimersRef.current[id] = timer;
  };

  const stopProgressTicker = (id) => {
    if (progressTimersRef.current[id]) {
      clearInterval(progressTimersRef.current[id]);
      delete progressTimersRef.current[id];
    }
  };

  const gateProgressCard = (id, { message, approveLabel, onApprove, onCancel }) => {
    stopProgressTicker(id);
    patchProgressCard(id, { awaitingApproval: true, gateMessage: message, gateApproveLabel: approveLabel, gateBusy: false, onApprove, onCancel, note: null });
  };

  const completeProgressCard = (id, overrides = {}) => {
    stopProgressTicker(id);
    patchProgressCard(id, (c) => ({
      activeIndex: c.steps.length - 1,
      completed: true,
      awaitingApproval: false,
      note: null,
      ...overrides,
    }));
  };

  const failProgressCard = (id) => {
    stopProgressTicker(id);
    patchProgressCard(id, { completed: true, awaitingApproval: false, note: null, ctaLabel: null });
  };

  useEffect(() => {
    if (!resizing) return;
    const handleMove = (e) => {
      const delta = resizeStartRef.current.x - e.clientX;
      const next = Math.min(560, Math.max(280, resizeStartRef.current.width + delta));
      setPanelWidth(next);
    };
    const handleUp = () => setResizing(false);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [resizing]);

  const handleResizeStart = (e) => {
    e.preventDefault();
    resizeStartRef.current = { x: e.clientX, width: panelWidth };
    setResizing(true);
  };

  const toggleVoiceInput = () => {
    if (!SpeechRecognitionAPI) return;

    if (listening) {
      const active = recognitionRef.current;
      if (active) {
        active.manualStop = true;
        active.stop();
      }
      return;
    }

    const recognition = new SpeechRecognitionAPI();
    recognition.lang = "en-US";
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.manualStop = false;
    // Voice appends to whatever is already typed, and finalised speech is
    // kept here because each restart below resets event.results.
    recognition.baseText = editableRef.current?.textContent || "";
    recognition.finalText = "";

    recognition.onresult = (event) => {
      let finalChunk = "";
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) finalChunk += result[0].transcript;
        else interim += result[0].transcript;
      }
      if (finalChunk) recognition.finalText += finalChunk;
      const transcript = `${recognition.baseText}${recognition.finalText}${interim}`;
      if (editableRef.current) editableRef.current.textContent = transcript;
      setEditableText(transcript);
    };

    // Chrome ends the session on its own after a pause even in continuous
    // mode, so restart it and keep the mic live until the recruiter clicks
    // it again — pausing to think shouldn't cut them off mid-sentence.
    recognition.onend = () => {
      if (recognition.manualStop) {
        setListening(false);
        return;
      }
      try {
        recognition.start();
      } catch (error) {
        setListening(false);
      }
    };

    recognition.onerror = (event) => {
      // A silent stretch or our own stop() aren't failures — let onend
      // decide whether to resume.
      if (event.error === "no-speech" || event.error === "aborted") return;
      recognition.manualStop = true;
      setListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  // Section names as the recruiter actually sees them (Ranking/Insights/Email
  // tabs), not the internal "list"/"insights"/"email" keys — the backend
  // folds this straight into the prompt as prose.
  const SECTION_LABELS = { list: "Ranking", insights: "Insights", email: "Email" };

  const buildCurrentContext = () => {
    const list = candidates || [];
    const selected = selectedCandidateId
      ? list.find(c => c.candidateId === selectedCandidateId)
      : null;
    return {
      activeTab: "ranking",
      activeSection: SECTION_LABELS[activeSection] || activeSection || null,
      selectedJob: jobId ? { jobId, title: jobTitle } : null,
      activeFilter: filterDescription || null,
      // The candidate currently open in the detail pane — this is what "this
      // candidate" / "them" / "their resume" refers to when the recruiter
      // doesn't name someone explicitly.
      selectedCandidate: selected ? {
        candidateId: selected.candidateId,
        candidateName: selected.candidateName,
        score: selected.score,
        summary: selected.summary,
        strengths: selected.strengths || [],
        gaps: selected.gaps || [],
        skills: selected.skills || [],
        yearsExperience: selected.yearsExperience ?? null,
      } : null,
      visibleCandidates: list.map(c => ({
        candidateId: c.candidateId,
        candidateName: c.candidateName,
        score: c.score,
        summary: c.summary,
        strengths: c.strengths || [],
        gaps: c.gaps || [],
        skills: c.skills || [],
        yearsExperience: c.yearsExperience ?? null,
      })),
      // The currently open Email Report, if any — grounds "these
      // candidates"/"send it"/"how many did we email" without re-asking.
      emailReport: emailReportSummary || null,
    };
  };

  const appendAssistantText = (text) => {
    setMessages(prev => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last && last.assistant !== undefined && last._streaming) {
        next[next.length - 1] = { ...last, assistant: (last.assistant || "") + text };
      } else {
        next.push({ assistant: text, _streaming: true });
      }
      return next;
    });
  };

  const finalizeStreamingBubble = () => {
    setMessages(prev => prev.map(m => (m._streaming ? { ...m, _streaming: false } : m)));
  };

  const sendMessage = async (value) => {
    const raw = value !== undefined ? value : (editableRef.current?.textContent || "");
    const question = String(raw || "").trim();
    if (!question || loading) return;

    setMessages(prev => [...prev, { user: question }]);
    if (editableRef.current) editableRef.current.innerHTML = "";
    setEditableText("");
    setClarification(null);
    setPendingProposal(null);
    setLoading(true);
    setStatusText("");

    // First check whether this is actually a request to draft candidate
    // emails rather than a question — generates the emails and leaves an
    // Email Report (like the Ranking/Insights reports) in Section 1 instead
    // of routing into normal Q&A.
    if (requestBulkEmail) {
      let emailIntent = null;
      try {
        emailIntent = await callRecruitmentAIAction("parse_email_request", { query: question });
      } catch (e) {
        emailIntent = null;
      }
      if (emailIntent && !emailIntent.error && emailIntent.isEmailRequest) {
        const genId = pushProgressCard("email_generation");
        startProgressTicker(genId, 2);
        try {
          const result = await requestBulkEmail(emailIntent);
          if (result.ok) {
            completeProgressCard(genId, {
              reportId: result.reportId,
              bulk: { current: result.count, total: result.count, unit: t("emails"), verb: t("drafted") },
            });
            setMessages(prev => [...prev, {
              reportCard: { mode: "email", jobId: result.jobId, jobTitle: result.jobTitle, count: result.count, reportId: result.reportId },
            }]);

            const runSend = (sendId) => {
              startProgressTicker(sendId, 1);
              return onSendAllEmails?.(result.reportId).then(() => {
                completeProgressCard(sendId, {
                  bulk: { current: result.count, total: result.count, unit: t("emails"), verb: t("sent") },
                  ctaLabel: t("View Results"),
                  onCta: () => onOpenEmailReport?.(result.reportId),
                });
              });
            };

            if (executionMode === "auto") {
              const sendId = pushProgressCard("email_sending", {
                reportId: result.reportId,
                bulk: { current: 0, total: result.count, unit: t("emails"), verb: t("sent") },
              });
              try {
                await runSend(sendId);
                setMessages(prev => [...prev, { assistant: t("Sent {{count}} emails automatically.", { count: result.count }) }]);
              } catch (sendError) {
                console.error("Recruitment AI auto-send error:", sendError);
                failProgressCard(sendId);
                setMessages(prev => [...prev, { assistant: t("Sorry, some emails couldn't be sent.") }]);
              }
            } else {
              const sendId = pushProgressCard("email_sending", {
                reportId: result.reportId,
                bulk: { current: 0, total: result.count, unit: t("emails"), verb: t("sent") },
                ctaLabel: t("Review Emails"),
                onCta: () => onOpenEmailReport?.(result.reportId),
              });
              gateProgressCard(sendId, {
                message: t("{{count}} emails are ready to send. Would you like me to send them?", { count: result.count }),
                approveLabel: t("Approve & Send"),
                onApprove: async () => {
                  patchProgressCard(sendId, { awaitingApproval: false, activeIndex: 1 });
                  try {
                    await runSend(sendId);
                    setMessages(prev => [...prev, { assistant: t("Sent {{count}} emails.", { count: result.count }) }]);
                  } catch (sendError) {
                    console.error("Recruitment AI send-all error:", sendError);
                    failProgressCard(sendId);
                    setMessages(prev => [...prev, { assistant: t("Sorry, some emails couldn't be sent. Please try again.") }]);
                  }
                },
                onCancel: () => {
                  failProgressCard(sendId);
                  setMessages(prev => [...prev, { assistant: t("Okay, I won't send them yet — you can review and send each one from the Email report.") }]);
                },
              });
            }
          } else {
            failProgressCard(genId);
            setMessages(prev => [...prev, { assistant: result.message || t("Couldn't find candidates matching that request.") }]);
          }
        } catch (error) {
          console.error("Recruitment AI bulk email error:", error);
          failProgressCard(genId);
          setMessages(prev => [...prev, { assistant: t("Sorry, I couldn't generate those emails. Please try again.") }]);
        }
        setLoading(false);
        setStatusText("");
        return;
      }
    }

    // The bubble above already shows the recruiter's raw question — this
    // grounded version (with no-invention / name-mention instructions) is
    // only what gets sent to the model, never displayed.
    const outgoingMessage = active
      ? `${question}\n\n(Answer using ONLY the candidate data provided in context — their real scores, skills, experience, strengths and gaps. Never invent or guess a skill, score, or qualification that isn't present in the data. Always mention the relevant candidate's name(s) by name in your answer.)`
      : question;

    let assistantTextAccum = "";
    const stageUpdates = [];

    try {
      await streamRecruitmentChat({
        message: outgoingMessage,
        conversationHistory: conversationHistoryRef.current,
        currentContext: buildCurrentContext(),
        onEvent: (evt) => {
          switch (evt.type) {
            case "tool_start":
              if (evt.tool === "analyze_resume") {
                finalizeStreamingBubble();
                const progressId = pushProgressCard("resume_analysis");
                resumeProgressIdRef.current = progressId;
                startProgressTicker(progressId, 2);
              } else {
                setStatusText(`Checking ${evt.tool.replace(/_/g, " ")}...`);
              }
              break;
            case "tool_done":
              setStatusText("");
              break;
            case "generating":
              setStatusText("");
              break;
            case "text_delta":
              assistantTextAccum += evt.text;
              appendAssistantText(evt.text);
              break;
            case "clarification":
              setClarification({ question: evt.question, options: evt.options || [] });
              break;
            case "recruitment_action_proposal":
              if (evt.action === "propose_candidate_stage_update") {
                // "Move the top 5 to Interview" arrives as one proposal per
                // candidate. Collect them and run the whole batch through a
                // single card once the stream ends, instead of stacking one
                // card (and one approval) per candidate.
                stageUpdates.push(evt);
              } else if (executionMode === "auto") {
                applyProposalAndNotify(evt.action, evt.payload);
              } else {
                setPendingProposal({ action: evt.action, payload: evt.payload, summary: evt.summary });
              }
              break;
            case "resume_analysis":
              finalizeStreamingBubble();
              // The score card belongs with its action in Section 1, not in
              // the chat thread — carrying it on the progress card means
              // "View progress" on that card reopens the result too.
              if (resumeProgressIdRef.current) {
                completeProgressCard(resumeProgressIdRef.current, { resumeResult: evt });
                resumeProgressIdRef.current = null;
              } else {
                const id = pushProgressCard("resume_analysis");
                completeProgressCard(id, { resumeResult: evt });
              }
              break;
            case "error":
              setMessages(prev => [...prev, { assistant: `Sorry — ${evt.message}` }]);
              break;
            case "done":
              setStatusText("");
              break;
            default:
              break;
          }
        },
      });

      finalizeStreamingBubble();
      const nextHistory = [...conversationHistoryRef.current, { role: "user", content: question }];
      if (assistantTextAccum) nextHistory.push({ role: "assistant", content: assistantTextAccum });
      conversationHistoryRef.current = nextHistory.slice(-8);

      if (stageUpdates.length) runStageUpdates(stageUpdates);
    } catch (error) {
      console.error("Recruitment AI chatbot error:", error);
      setMessages(prev => [...prev, { assistant: "Sorry, I couldn't get a response from the AI service. Please try again." }]);
    } finally {
      setLoading(false);
      setStatusText("");
    }
  };

  const mentionQuery = mentionOpen ? (activeMentionQuery(editableText) || "") : "";
  const filteredJobs = mentionOpen
    ? (jobs || []).filter((j) => j.title?.toLowerCase().includes(mentionQuery.toLowerCase()))
    : [];

  const handleEditableInput = () => {
    const text = editableRef.current?.textContent || "";
    setEditableText(text);
    const q = activeMentionQuery(text);
    if (q !== null && (jobs || []).length) {
      setMentionOpen(true);
      setMentionActiveIndex(0);
    } else {
      setMentionOpen(false);
    }
  };

  const placeCaretAtEnd = (root) => {
    const range = document.createRange();
    range.selectNodeContents(root);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  };

  // The trailing "@query" text is always the tail of the contentEditable's
  // text — strip exactly that many characters off the last text node(s)
  // before splicing in the (non-editable) mention chip.
  const removeTrailingChars = (root, count) => {
    let remaining = count;
    for (let i = root.childNodes.length - 1; i >= 0 && remaining > 0; i--) {
      const node = root.childNodes[i];
      if (node.nodeType !== Node.TEXT_NODE) break;
      const len = node.textContent.length;
      if (len <= remaining) {
        remaining -= len;
        root.removeChild(node);
      } else {
        node.textContent = node.textContent.slice(0, len - remaining);
        remaining = 0;
      }
    }
  };

  // Splices an atomic, non-editable job chip into the input in place of the
  // "@query" the user just typed — used for a plain row/Enter selection,
  // which only picks the job so the recruiter can keep typing a question.
  const insertMentionChip = (job) => {
    const root = editableRef.current;
    if (!root) return;
    const text = root.textContent || "";
    const m = /(?:^|\s)@([^\s@]*)$/.exec(text);
    removeTrailingChars(root, m ? m[1].length + 1 : 0);

    const chip = document.createElement("span");
    chip.contentEditable = "false";
    chip.dataset.jobId = job.jobId;
    Object.assign(chip.style, MentionChipStyle);
    chip.textContent = `@${job.title}`;
    root.appendChild(chip);
    root.appendChild(document.createTextNode(" "));

    placeCaretAtEnd(root);
    root.focus();
    setMentionOpen(false);
    setEditableText(root.textContent || "");
  };

  // The Rank/Insights icons in the mention list are a staged action, not a
  // freeform question — they behave like sending "@JobTitle" as a chat
  // message: it's posted to the thread, the input clears, the ranking or
  // insights call runs, and a clickable report card is left behind so the
  // recruiter can reopen that (cached) report in Section 1 later.
  const handleMentionAction = async (job, mode) => {
    const root = editableRef.current;
    const text = root?.textContent || "";
    const m = /(?:^|\s)@([^\s@]*)$/.exec(text);
    const prefix = m ? text.slice(0, text.length - (m[1].length + 1)) : text;
    const displayText = `${prefix}@${job.title}`.trim();

    setMentionOpen(false);
    if (root) root.innerHTML = "";
    setEditableText("");

    setMessages(prev => [...prev, { user: displayText }]);
    setLoading(true);

    // No ProgressCard here — Section 1 already renders a bulk, real-count
    // progress checklist for ranking/insights (JobsList's own "Analyzing
    // Resume" view), so this would just be a redundant second one.
    try {
      await onSelectJob?.(job.jobId, mode);
      setMessages(prev => [...prev, { reportCard: { mode, jobId: job.jobId, jobTitle: job.title } }]);
    } catch (error) {
      console.error("Recruitment AI mention action error:", error);
      setMessages(prev => [...prev, { assistant: t("Sorry, I couldn't generate that report. Please try again.") }]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditableKeyDown = (e) => {
    if (mentionOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionActiveIndex((i) => Math.min(i + 1, filteredJobs.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionActiveIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Escape") {
        setMentionOpen(false);
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        if (filteredJobs.length) insertMentionChip(filteredJobs[mentionActiveIndex]);
        return;
      }
    }
    if (e.key === "Enter") {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleClarificationOption = (option) => {
    setClarification(null);
    sendMessage(option.label || option.value);
  };

  // Shared by the manual "Confirm" button (Approval Mode) and Auto Mode,
  // which calls this immediately instead of ever setting pendingProposal.
  const applyProposalAndNotify = async (action, payload) => {
    setApplyingProposal(true);
    try {
      const result = await applyProposal(action, payload);
      const ok = result && (result.code === STATUS_CODE.SUCCESS || result.code === undefined);
      setMessages(prev => [...prev, { assistant: ok ? "Done — updated." : "Sorry, that update failed. Please try again." }]);
      if (ok) {
        const refresh = refreshCallbackFor(action, { onCandidateUpdated, onJobUpdated });
        refresh?.();
      }
    } catch (error) {
      console.error("Recruitment AI proposal apply error:", error);
      setMessages(prev => [...prev, { assistant: "Sorry, that update failed. Please try again." }]);
    } finally {
      setApplyingProposal(false);
    }
  };

  // One card for a whole batch of stage moves ("move the top 5 to
  // Interview" is one proposal per candidate on the wire), stepping through
  // the candidates one at a time behind a single approval.
  const runStageUpdates = (updates) => {
    const total = updates.length;
    const stageLabel = updates[0].payload.newStageLabel;
    const single = total === 1;
    const bulkOf = (done) => ({
      current: done,
      total,
      unit: total === 1 ? t("candidate") : t("candidates"),
      verb: t("moved"),
    });

    const progressId = pushProgressCard("status_update", {
      description: single
        ? updates[0].summary
        : t("Moving {{count}} candidates to \"{{stage}}\".", { count: total, stage: stageLabel }),
      bulk: single ? null : bulkOf(0),
      currentItem: {
        title: updates[0].payload.candidateName,
        subtitle: t("Moving to \"{{stage}}\"", { stage: stageLabel }),
      },
      ctaLabel: t("View Candidate"),
      onCta: () => onViewCandidate?.(updates[0].payload.candidateId),
    });

    const applyAll = async () => {
      patchProgressCard(progressId, { awaitingApproval: false, activeIndex: 1 });
      let done = 0;
      let failed = 0;
      for (const update of updates) {
        patchProgressCard(progressId, {
          currentItem: {
            title: update.payload.candidateName,
            subtitle: t("Moving to \"{{stage}}\"", { stage: update.payload.newStageLabel }),
          },
        });
        try {
          const result = await applyProposal(update.action, update.payload);
          const ok = result && (result.code === STATUS_CODE.SUCCESS || result.code === undefined);
          if (ok) done += 1; else failed += 1;
        } catch (error) {
          console.error("Recruitment AI stage update error:", error);
          failed += 1;
        }
        if (!single) patchProgressCard(progressId, { bulk: bulkOf(done) });
      }

      completeProgressCard(progressId, single ? {} : { bulk: bulkOf(done) });
      if (done) refreshCallbackFor(updates[0].action, { onCandidateUpdated, onJobUpdated })?.();
      setMessages(prev => [...prev, {
        assistant: failed
          ? (done
              ? t("Moved {{done}} of {{total}} candidates — {{failed}} failed.", { done, total, failed })
              : t("Sorry, that update failed. Please try again."))
          : single
          ? t("Done — updated.")
          : t("Done — moved {{count}} candidates to \"{{stage}}\".", { count: done, stage: stageLabel }),
      }]);
    };

    if (executionMode === "auto") {
      startProgressTicker(progressId, 1);
      applyAll();
      return;
    }

    gateProgressCard(progressId, {
      message: single
        ? updates[0].summary
        : t("Move {{count}} candidates to \"{{stage}}\"?", { count: total, stage: stageLabel }),
      approveLabel: t("Approve & Continue"),
      onApprove: applyAll,
      onCancel: () => {
        failProgressCard(progressId);
        setMessages(prev => [...prev, { assistant: t("Okay, no changes made.") }]);
      },
    });
  };

  const handleProposalDecision = async (decision) => {
    if (!pendingProposal) return;
    if (decision === "reject") {
      setPendingProposal(null);
      setMessages(prev => [...prev, { assistant: "Okay, no changes made." }]);
      return;
    }
    const { action, payload } = pendingProposal;
    setPendingProposal(null);
    await applyProposalAndNotify(action, payload);
  };

  if (collapsed) {
    return (
      <RailOuter>
        <RailWrap onClick={onToggleCollapse} title={t("Expand AI Chatbot")}>
          <SmartToyOutlinedIcon style={{ fontSize: 18, color: PRIMARY }} />
          <RailLabel>{t("AI Chatbot")}</RailLabel>
        </RailWrap>
        <RailExpandButton type="button" onClick={onToggleCollapse} aria-label={t("Expand AI Chatbot")}>
          <ChevronLeftIcon />
        </RailExpandButton>
      </RailOuter>
    );
  }

  return (
    <PanelWrap style={{ height: maxHeight, width: panelWidth }}>
      <ResizeHandle className={resizing ? "dragging" : undefined} onMouseDown={handleResizeStart} />
      <PanelHeader>
        <PanelHeaderTitle>
          <SmartToyOutlinedIcon style={{ fontSize: 17, color: PRIMARY }} />
          {t("AI Chatbot")}
        </PanelHeaderTitle>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Tooltip
            title={
              executionMode === "auto"
                ? t("Auto Mode (Automatically execute actions)")
                : t("Approval Mode (Ask before taking action)")
            }
          >
            <ExecutionModeButton
              type="button"
              $auto={executionMode === "auto"}
              onClick={() => setExecutionMode((m) => (m === "approval" ? "auto" : "approval"))}
            >
              {executionMode === "auto" ? <BoltIcon /> : <PanToolIcon />}
            </ExecutionModeButton>
          </Tooltip>
          <Tooltip title={t("Guide")}>
            <CollapseButton type="button" onClick={() => setGuideOpen(true)}>
              <HelpOutlineIcon />
            </CollapseButton>
          </Tooltip>
          <Tooltip title={t("Collapse")}>
            <CollapseButton type="button" onClick={onToggleCollapse}>
              <ChevronRightIcon />
            </CollapseButton>
          </Tooltip>
        </div>
      </PanelHeader>

      <GuideModalStyle />
      <Modal
        open={guideOpen}
        onCancel={() => setGuideOpen(false)}
        footer={null}
        width={560}
        wrapClassName="guide-modal"
        bodyStyle={{ padding: 0 }}
        destroyOnClose
      >
        <GuideHeader>
          <GuideHeaderIcon><SmartToyOutlinedIcon /></GuideHeaderIcon>
          <div>
            <GuideTitle>{t("How to use the AI Recruiter")}</GuideTitle>
            <GuideSubtitle>{t("Ask in plain English — here's what it can do.")}</GuideSubtitle>
          </div>
        </GuideHeader>

        <GuideBody>
          <GuideStep>
            <GuideStepNumber>1</GuideStepNumber>
            <div>
              <GuideStepTitle>{t("Pick a job first")}</GuideStepTitle>
              <GuideStepText>
                {t("Type")} <code>@</code> {t("and choose a job (e.g.")} <code>@Backend Engineer NodeJS</code>
                {t("). Everything you ask after that applies to that job's candidates.")}
              </GuideStepText>
              <GuideStepText style={{ marginTop: 8 }}>
                {t("Each job in that list also has two shortcut buttons that score the candidates straight away:")}
              </GuideStepText>
              <GuideModeRow>
                <GuideModeIcon><BarChartOutlinedIcon /></GuideModeIcon>
                <GuideStepText>
                  <strong>{t("Rank only")}</strong> — {t("scores every candidate and opens the ranked list.")}
                </GuideStepText>
              </GuideModeRow>
              <GuideModeRow>
                <GuideModeIcon><InsightsOutlinedIcon /></GuideModeIcon>
                <GuideStepText>
                  <strong>{t("Rank + Insights")}</strong> — {t("same scoring, plus the summary view: skills, experience, locations and top candidates.")}
                </GuideStepText>
              </GuideModeRow>
            </div>
          </GuideStep>

          <GuideStep>
            <GuideStepNumber>2</GuideStepNumber>
            <div style={{ minWidth: 0 }}>
              <GuideStepTitle>{t("Then just ask")}</GuideStepTitle>

              <GuideGroupLabel>{t("Screen & rank")}</GuideGroupLabel>
              <GuideExample>{t("Rank the candidates for this job")}</GuideExample>
              <GuideExample>{t("Show me insights for this job")}</GuideExample>
              <GuideExample>{t("Analyze this candidate's resume")}</GuideExample>

              <GuideGroupLabel>{t("Find the right people")}</GuideGroupLabel>
              <GuideExample>{t("Who has GraphQL experience?")}</GuideExample>
              <GuideExample>{t("Candidates with 5+ years in Nairobi")}</GuideExample>
              <GuideExample>{t("How does this candidate compare with others?")}</GuideExample>

              <GuideGroupLabel>{t("Take action")}</GuideGroupLabel>
              <GuideExample>{t("Send an interview invitation email to the top 5 candidates")}</GuideExample>
              <GuideExample>{t("Move Neha Sharma to the Interview stage")}</GuideExample>
              <GuideExample>{t("Move the top 3 candidates to Shortlist")}</GuideExample>
            </div>
          </GuideStep>

          <GuideStep>
            <GuideStepNumber>3</GuideStepNumber>
            <div>
              <GuideStepTitle>{t("Choose how it acts")}</GuideStepTitle>
              <GuideStepText>{t("Use the icon at the top of this panel to switch modes.")}</GuideStepText>
              <GuideModeRow>
                <GuideModeIcon><PanToolIcon /></GuideModeIcon>
                <GuideStepText>{t("Approval mode — asks before it changes anything.")}</GuideStepText>
              </GuideModeRow>
              <GuideModeRow>
                <GuideModeIcon $auto><BoltIcon /></GuideModeIcon>
                <GuideStepText>{t("Auto mode — performs the action right away.")}</GuideStepText>
              </GuideModeRow>
            </div>
          </GuideStep>

          <GuideStep>
            <GuideStepNumber>4</GuideStepNumber>
            <div>
              <GuideStepTitle>{t("Follow what it's doing")}</GuideStepTitle>
              <GuideStepText>
                {t("Every action leaves a progress card with its steps. Click \"View progress\" to bring that action back up, \"Review Emails\" to read drafts before they're sent, and \"View report\" to reopen a ranking or email report.")}
              </GuideStepText>
            </div>
          </GuideStep>
        </GuideBody>
      </Modal>

      <Messages ref={containerRef}>
        {messages.map((message, index) => (
          message.progressCardId
            ? (progressCards[message.progressCardId]
                ? (
                  <MiniProgressCard
                    key={index}
                    card={progressCards[message.progressCardId]}
                    onShow={() => onShowProgress?.(progressCards[message.progressCardId])}
                  />
                )
                : null)
            : message.reportCard
            ? (
              <ReportCard
                key={index}
                card={message.reportCard}
                onOpen={() => {
                  if (message.reportCard.mode !== "email") {
                    onSelectJob?.(message.reportCard.jobId, message.reportCard.mode);
                    return;
                  }
                  // Bring back the send's own step snapshot alongside the
                  // report, so the email context isn't just a bare modal.
                  const reportId = message.reportCard.reportId;
                  const cards = Object.values(progressCards).filter((c) => c.reportId === reportId);
                  const sendCard = cards.find((c) => c.flowKey === "email_sending") || cards[0];
                  if (sendCard) onShowProgress?.(sendCard);
                  onOpenEmailReport?.(reportId);
                }}
              />
            )
            : (
              <Bubble key={index} $user={Boolean(message.user)}>
                {!message.user && (
                  <BotAvatar>
                    <AutoAwesomeIcon />
                  </BotAvatar>
                )}
                <div>
                  {message.user ? message.user : <MarkdownText text={message.assistant} />}
                </div>
              </Bubble>
            )
        ))}

        {bulkProgress && <MiniProgressCard card={bulkProgress} />}

        {clarification && (
          <AccentCard $accent={PRIMARY}>
            <CardLabel $accent={PRIMARY}>{t("Quick question")}</CardLabel>
            <CardBody>{clarification.question}</CardBody>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {clarification.options.map((opt, i) => (
                <SuggestionChip key={opt.value || i} size="small" onClick={() => handleClarificationOption(opt)}>
                  {opt.label}
                </SuggestionChip>
              ))}
            </div>
          </AccentCard>
        )}

        {pendingProposal && (
          <AccentCard $accent={COLORS.SUCCESS}>
            <CardLabel $accent={COLORS.SUCCESS}>{t("Proposed change")}</CardLabel>
            <CardBody>{pendingProposal.summary}</CardBody>
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                size="small"
                style={{ background: COLORS.SUCCESS, borderColor: COLORS.SUCCESS, color: "#fff" }}
                icon={<CheckOutlinedIcon style={{ fontSize: 13 }} />}
                loading={applyingProposal}
                onClick={() => handleProposalDecision("confirm")}
              >
                {t("Confirm")}
              </Button>
              <Button
                size="small"
                danger
                ghost
                disabled={applyingProposal}
                onClick={() => handleProposalDecision("reject")}
              >
                {t("Reject")}
              </Button>
            </div>
          </AccentCard>
        )}

        {statusText && (
          <StatusLine>
            <TypingBubble style={{ padding: "6px 10px" }}>
              <span /><span /><span />
            </TypingBubble>
            {statusText}
          </StatusLine>
        )}

        {loading && !statusText && (
          <TypingBubble>
            <span /><span /><span />
          </TypingBubble>
        )}
      </Messages>

      <Footer style={{ position: "relative" }}>
        {mentionOpen && (
          <MentionList>
            {filteredJobs.length > 0 ? (
              filteredJobs.map((job, i) => (
                <MentionItem key={job.jobId} $active={i === mentionActiveIndex}>
                  <MentionItemText
                    onMouseDown={(e) => {
                      e.preventDefault();
                      insertMentionChip(job);
                    }}
                  >
                    {job.title}
                  </MentionItemText>
                  <MentionActions>
                    <Tooltip title={t("Rank only")}>
                      <MentionActionBtn
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleMentionAction(job, "rank");
                        }}
                      >
                        <BarChartOutlinedIcon />
                      </MentionActionBtn>
                    </Tooltip>
                    <Tooltip title={t("Rank + Insights")}>
                      <MentionActionBtn
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleMentionAction(job, "insights");
                        }}
                      >
                        <InsightsOutlinedIcon />
                      </MentionActionBtn>
                    </Tooltip>
                  </MentionActions>
                </MentionItem>
              ))
            ) : (
              <MentionEmpty>{t("No matching jobs")}</MentionEmpty>
            )}
          </MentionList>
        )}
        <EditableInput
          ref={editableRef}
          contentEditable={!loading}
          suppressContentEditableWarning
          data-placeholder={t("Type @ to pick a job, then ask your question...")}
          onInput={handleEditableInput}
          onKeyDown={handleEditableKeyDown}
        />
        {SpeechRecognitionAPI && (
          <Tooltip title={listening ? t("Stop listening") : t("Speak your question")}>
            <VoiceButton
              $listening={listening}
              disabled={loading}
              onClick={toggleVoiceInput}
              icon={listening ? <MicIcon style={{ fontSize: 16 }} /> : <MicNoneOutlinedIcon style={{ fontSize: 16 }} />}
            />
          </Tooltip>
        )}
        <SendButton
          icon={<SendOutlinedIcon style={{ color: "#fff", fontSize: 15 }} />}
          onClick={() => sendMessage()}
          loading={loading}
        />
      </Footer>
    </PanelWrap>
  );
}

export default RecruitmentChatPanel;
